import { SubscriptionStatus } from "@features/Subscription/subscription.types";
import { ComputedCommunicationPreferences } from "@shared/apis/sidekick/generated";
import { create, StateCreator } from "zustand";
import { persist, createJSONStorage, PersistOptions } from "zustand/middleware";

export enum UserRoles {
  Admin = "admin",
  User = "user",
}

export enum UserChatInterfacePermissions {
  FullAccess = "*",
  App = "app",
  Native = "native",
}

type Theme = "light" | "dark" | "system";
export interface User {
  id: string;
  name: string;
  firstName: string;
  email: string;
  authTokenExp: string | null;
  avatar: string | null;
  timezone: string | null;
  countryState: string | null;
  country: string | null;
  phoneNumber: string | null;
  mlsId: string;
  officeName: string;
  roles: UserRoles[];
  allowedMlsProviders: string[];
  enabledMlsProviders: string[];
  allowedMlsProvidersLabels: string[];
  allowedChatInterfaces: UserChatInterfacePermissions[];
  trackingId: string;
  subscriptionPlanId: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  hash: string;
  createdOn: Date;
  subscriptionPlanTier: "basic" | "pro";
  communicationPreferences: ComputedCommunicationPreferences;
  modules: string[];
}
export interface AuthStoreState {
  user: User;
  preferences: {
    showChatDebugMessages: boolean;
    showChatPromptRequestDuration: boolean;
    renderMarkdownOnUserMessage: boolean;
    theme: Theme;
  };
  isAuthenticated: boolean;
  authTokenStatus: "IDLE" | "VALID" | "EXPIRED" | null;
  isValidatingCookies: boolean;
  authError: { message: string; type: string } | null;
  sessionTerminated: boolean;
}

const USER_STORAGE_ID = "@sidekick:user-storage";

interface AuthStoreActions {
  authenticate: (user: User) => void;
  setPartialUserData: (partialUser: Partial<User>) => void;
  updatePreferences: (
    preferences: Partial<AuthStoreState["preferences"]>,
  ) => void;
  logout: () => void;
  userNotAuthenticated: () => void;
  userSessionExpired: () => void;
  setAuthTokenStatus: (status: AuthStoreState["authTokenStatus"]) => void;
  userInvalidToken: () => void;
}

const initialState: AuthStoreState = {
  user: {
    id: "",
    name: "",
    firstName: "",
    email: "",
    authTokenExp: null,
    avatar: "",
    timezone: null,
    countryState: null,
    country: null,
    phoneNumber: null,
    mlsId: "",
    officeName: "",
    roles: [],
    allowedChatInterfaces: [],
    allowedMlsProviders: [],
    enabledMlsProviders: [],
    allowedMlsProvidersLabels: [],
    trackingId: "",
    subscriptionPlanId: null,
    subscriptionStatus: null,
    hash: "",
    createdOn: new Date("1970-01-01"),
    subscriptionPlanTier: "basic",
    communicationPreferences: {} as ComputedCommunicationPreferences,
    modules: [],
  },
  preferences: {
    showChatDebugMessages: false,
    showChatPromptRequestDuration: false,
    renderMarkdownOnUserMessage: false,
    theme: "light",
  },
  isAuthenticated: false,
  authTokenStatus: "IDLE",
  isValidatingCookies: true,
  authError: null,
  sessionTerminated: false,
};

type AuthStore = AuthStoreState & AuthStoreActions;

type MyPersist = (
  config: StateCreator<AuthStore>,
  options: PersistOptions<{
    preferences: AuthStoreState["preferences"];
  }>,
) => StateCreator<AuthStore>;

export const useAuthStore = create<AuthStore>()(
  (persist as MyPersist)(
    (set) => ({
      ...initialState,
      authenticate: (user) =>
        set((state) => ({
          user: { ...user },
          isAuthenticated: true,
          isValidatingCookies: false,
          sessionTerminated: false,
          preferences: {
            ...initialState.preferences,
            ...state.preferences,
          },
        })),
      setPartialUserData: (partialUser) =>
        set((state) => {
          Object.entries(partialUser).forEach(([key, value]) => {
            if (value === undefined) {
              delete partialUser[key as keyof typeof partialUser];
            }
          });
          const newUser = {
            ...state.user,
            ...(partialUser as User),
          };
          return {
            user: newUser,
          };
        }),
      updatePreferences: (preferences) =>
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...preferences,
          },
        })),
      logout: () => {
        sessionStorage.clear();
        set(() => ({
          user: initialState.user,
          isAuthenticated: false,
          authTokenStatus: null,
          authError: initialState.authError,
          sessionTerminated: true,
        }));
      },
      userNotAuthenticated: () =>
        set((state) => {
          if (state.isAuthenticated) {
            sessionStorage.clear();
          }
          return {
            user: initialState.user,
            isAuthenticated: false,
            isValidatingCookies: false,
            authTokenStatus: null,
            sessionTerminated: true,
          };
        }),
      userSessionExpired: () => {
        sessionStorage.clear();
        set(() => ({
          user: initialState.user,
          isAuthenticated: false,
          isValidatingCookies: false,
          authTokenStatus: null,
          sessionTerminated: true,
          authError: {
            message: "Your session has expired. Please sign in again.",
            type: "SESSION_EXPIRED",
          },
        }));
      },
      userInvalidToken: () => {
        sessionStorage.clear();
        set(() => ({
          user: initialState.user,
          isAuthenticated: false,
          isValidatingCookies: false,
          authTokenStatus: null,
          sessionTerminated: true,
          authError: {
            message: "Your session has expired. Please sign in again.",
            type: "INVALID_TOKEN",
          },
        }));
      },
      setAuthTokenStatus: (authTokenStatus) =>
        set(() => ({
          authTokenStatus,
        })),
    }),
    {
      name: USER_STORAGE_ID,
      storage: createJSONStorage(() => localStorage),
      // `user` holds PII and entitlements, so it's kept in memory only and
      // re-fetched on every load instead of persisted to localStorage.
      partialize: (state) => ({
        preferences: state.preferences,
      }),
      version: 1,
      // Sessions persisted before a field was added to preferences rehydrate
      // without it, since the default persist merge replaces `preferences`
      // wholesale rather than deep-merging. Backfilling against current
      // defaults here means every consumer can trust the full shape instead
      // of guarding for missing fields itself.
      migrate: (persistedState) => {
        const state = persistedState as
          | { preferences?: Partial<AuthStoreState["preferences"]> }
          | undefined;
        return {
          preferences: { ...initialState.preferences, ...state?.preferences },
        };
      },
    },
  ),
);
